import React, { useState, useEffect } from 'react';
import {
  User, Mail, Shield, Smartphone, Globe, Download, QrCode, Clock, Database,
  Wifi, AlertCircle, CheckCircle, X, Eye, CreditCard, Info, Activity, TrendingUp,
  Loader2, RefreshCw
} from 'lucide-react';
import { db } from './firebase';
import { collection, query, where, orderBy, onSnapshot, doc, updateDoc } from 'firebase/firestore';
import { useTranslation } from './useTranslation';

const BASE_URL = 'https://olahbackend.gaia-ml.com';

const Profile = ({ user, showAuthModal, setShowAuthModal }) => {
  const { t } = useTranslation();

  const [userEsims, setUserEsims] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [deletingEsim, setDeletingEsim] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [esimToDelete, setEsimToDelete] = useState(null);
  const [showQrModal, setShowQrModal] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [selectedEsim, setSelectedEsim] = useState(null);
  const [loadingBundleStatus, setLoadingBundleStatus] = useState(false);
  const [bundleStatusData, setBundleStatusData] = useState(null);
  const [bundleStatusError, setBundleStatusError] = useState(null);

  useEffect(() => {
    if (!user) setShowAuthModal(true);
  }, [user, setShowAuthModal]);

  useEffect(() => {
    if (!user) return;
    const unsubscribe = onSnapshot(
      query(collection(db, 'user_esims'), where('user_id', '==', user.uid), orderBy('purchased_at', 'desc')),
      (snapshot) => {
        try {
          const esims = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
          setUserEsims(esims);
          setLoading(false);
          setError(null);
        } catch (e) {
          console.error('Error processing eSIMs:', e);
          setError(t('profile.errors.load'));
          setLoading(false);
        }
      },
      (e) => {
        console.error('Firebase listener error:', e);
        setError(t('profile.errors.load'));
        setLoading(false);
      }
    );
    return () => unsubscribe();
  }, [user, t]);

  const handleDeleteEsim = (esim) => {
    setEsimToDelete(esim);
    setShowDeleteModal(true);
  };

  const confirmDeleteEsim = async () => {
    if (!esimToDelete) return;
    setDeletingEsim(esimToDelete.id);
    try {
      await updateDoc(doc(db, 'user_esims', esimToDelete.id), { hidden: true, hidden_at: new Date() });
      setShowDeleteModal(false);
      setEsimToDelete(null);
    } catch (e) {
      console.error('Error deleting eSIM:', e);
      alert(t('profile.errors.delete'));
    } finally {
      setDeletingEsim(null);
    }
  };

  const handleShowQr = (esim) => {
    setSelectedEsim(esim);
    setShowQrModal(true);
  };

  const fetchBundleStatus = async (esim) => {
    if (!esim || !user) return;
    setLoadingBundleStatus(true);
    setBundleStatusError(null);
    setBundleStatusData(null);
    try {
      const token = await user.getIdToken();
      const response = await fetch(`${BASE_URL}/api/esims/${esim.id}/status/`, {
        method: 'GET',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }
      });
      if (response.ok) {
        const data = await response.json();
        setBundleStatusData(data);
      } else {
        const errorData = await response.json();
        setBundleStatusError(errorData.error || t('profile.errors.bundle'));
      }
    } catch (e) {
      console.error('Error fetching bundle status:', e);
      setBundleStatusError(t('profile.errors.network'));
    } finally {
      setLoadingBundleStatus(false);
    }
  };

  const handleShowDetails = async (esim) => {
    setSelectedEsim(esim);
    setShowDetailsModal(true);
    await fetchBundleStatus(esim);
  };

  const handleRefreshBundleStatus = async () => {
    if (selectedEsim) await fetchBundleStatus(selectedEsim);
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return t('profile.common.na');
    try {
      const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
      return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
    } catch {
      return t('profile.common.invalidDate');
    }
  };

  const formatFullDate = (timestamp) => {
    if (!timestamp) return t('profile.common.na');
    try {
      const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
      return date.toLocaleDateString('en-US', {
        weekday: 'long', year: 'numeric', month: 'long', day: 'numeric', hour: '2-digit', minute: '2-digit', timeZoneName: 'short'
      });
    } catch {
      return t('profile.common.invalidDate');
    }
  };

  const getStatusColor = (status) => {
    switch ((status || '').toLowerCase()) {
      case 'purchased':
      case 'active': return 'bg-green-100 text-green-800';
      case 'cancelled': return 'bg-red-100 text-red-800';
      case 'expired': return 'bg-gray-100 text-gray-800';
      default: return 'bg-blue-100 text-blue-800';
    }
  };

  const getStatusIcon = (status) => {
    switch ((status || '').toLowerCase()) {
      case 'active': return <CheckCircle className="w-4 h-4" />;
      case 'expired': return <Clock className="w-4 h-4" />;
      case 'cancelled': return <AlertCircle className="w-4 h-4" />;
      default: return <Smartphone className="w-4 h-4" />;
    }
  };

  const getBundleStateColor = (bundleState) => {
    switch ((bundleState || '').toLowerCase()) {
      case 'active': return 'bg-green-100 text-green-800';
      case 'queued':
      case 'processing': return 'bg-yellow-100 text-yellow-800';
      case 'depleted': return 'bg-orange-100 text-orange-800';
      case 'expired':
      case 'lapsed': return 'bg-gray-100 text-gray-800';
      case 'revoked': return 'bg-red-100 text-red-800';
      default: return 'bg-blue-100 text-blue-800';
    }
  };

  const getBundleStateIcon = (bundleState) => {
    switch ((bundleState || '').toLowerCase()) {
      case 'active': return <CheckCircle className="w-4 h-4" />;
      case 'queued':
      case 'processing': return <Clock className="w-4 h-4" />;
      case 'depleted': return <TrendingUp className="w-4 h-4" />;
      case 'expired':
      case 'lapsed': return <Clock className="w-4 h-4" />;
      case 'revoked': return <AlertCircle className="w-4 h-4" />;
      default: return <Activity className="w-4 h-4" />;
    }
  };

  const downloadInvoice = async (esim) => {
    try {
      const token = await user.getIdToken();
      const response = await fetch(`${BASE_URL}/api/esims/${esim.id}/invoice/`, {
        method: 'GET',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `invoice_${esim.id}.pdf`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
      } else {
        alert(t('profile.errors.invoice'));
      }
    } catch (e) {
      console.error('Error downloading invoice:', e);
      alert(t('profile.errors.invoice'));
    }
  };

  const copyToClipboard = (text, labelKey) => {
    navigator.clipboard.writeText(text).then(() => {
      console.log(`${t(labelKey)} ${t('profile.common.copied')}`);
    }).catch(() => {
      const textArea = document.createElement('textarea');
      textArea.value = text;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-white to-gray-50 flex items-center justify-center px-6">
        <div className="w-full max-w-md">
          <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6">
            <div className="flex items-center space-x-4 mb-6">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-r from-blue-500 to-purple-600 animate-pulse" />
              <div className="flex-1 space-y-2">
                <div className="h-3 rounded bg-gray-200 w-2/3 animate-pulse" />
                <div className="h-3 rounded bg-gray-200 w-1/3 animate-pulse" />
              </div>
            </div>
            <div className="space-y-3">
              {[0,1,2].map((i)=> (<div key={i} className="h-16 rounded-xl bg-gray-100 animate-pulse" />))}
            </div>
            <p className="text-sm text-gray-500 mt-6 text-center">{t('profile.loading')}</p>
          </div>
        </div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center px-6">
        <div className="text-center max-w-md">
          <Shield className="w-16 h-16 text-blue-600 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-900 mb-2">{t('profile.auth.title')}</h2>
          <p className="text-gray-600 mb-6">{t('profile.auth.subtitle')}</p>
          <button
            onClick={() => setShowAuthModal(true)}
            className="px-6 py-3 bg-blue-600 text-white rounded-xl hover:bg-blue-700 transition font-medium shadow-sm"
          >
            {t('profile.auth.cta')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-white to-gray-50">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Profile Header */}
        <div className="relative overflow-hidden rounded-3xl border border-gray-200 bg-white shadow-sm mb-8">
          <div className="absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-blue-500 via-purple-500 to-pink-500" />
          <div className="p-6 md:p-8">
            <div className="flex items-center gap-6">
              <div className="flex-1 min-w-0">
                <h1 className="text-2xl md:text-3xl font-extrabold tracking-tight text-gray-900">
                  {user.displayName || t('profile.header.fallback')}
                </h1>
                <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm text-gray-600">
                  <div className="flex items-center truncate">
                    <Mail className="w-4 h-4 mr-2" />
                    <span className="truncate" title={user.email}>{user.email}</span>
                  </div>
                </div>
              </div> 
              <div className="hidden sm:flex items-center gap-4">
                <div className="text-right">
                  <div className="text-2xl font-bold text-blue-600">{userEsims.length}</div>
                  <div className="text-xs text-gray-500">
                    eSIMs purchased
                  </div> 
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* eSIMs Section */}
        <div className="rounded-3xl border border-gray-200 bg-white shadow-sm">
          <div className="p-6 md:p-8 border-b border-gray-200 flex items-center justify-between">
            <div>
              <h2 className="text-xl font-bold text-gray-900">{t('profile.list.title')}</h2>
              <p className="text-sm text-gray-500 mt-1">{t('profile.list.subtitle')}</p>
            </div>
            {userEsims.length > 0 && (
              <div className="text-sm text-gray-500">{t('profile.list.total', { count: userEsims.length })}</div>
            )}
          </div>

          {error && (
            <div className="m-6 md:mx-8 md:my-6 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl">
              <div className="flex items-center">
                <AlertCircle className="w-5 h-5 mr-2" />
                {error}
              </div>
            </div>
          )}

          {userEsims.length === 0 ? (
            <div className="text-center py-14 px-6">
              <div className="w-16 h-16 rounded-2xl bg-gray-100 flex items-center justify-center mx-auto mb-4">
                <Smartphone className="w-8 h-8 text-gray-400" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">{t('profile.empty.title')}</h3>
              <p className="text-gray-500 mb-6 max-w-md mx-auto">{t('profile.empty.subtitle')}</p>
              <a href="/browse" className="inline-flex items-center px-5 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-xl font-medium transition-colors shadow-sm">
                <Globe className="w-4 h-4 mr-2" />
                {t('profile.empty.cta')}
              </a>
              <div className="mt-6 text-xs text-gray-500">{t('profile.empty.tip')}</div>
            </div>
          ) : (
            <div className="divide-y divide-gray-100">
              {userEsims.map((esim) => (
                <div key={esim.id} className="p-6 md:p-8 transition-colors">
                  {/* Header Row */}
                  <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4 mb-4">
                    <div className="flex items-center gap-3">
                      <div>
                        <h3 className="font-semibold text-gray-900 leading-tight">
                          {t('profile.item.country')}: {esim.country_name || esim.country_code}
                        </h3>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(esim.status)}`} title={t('profile.item.statusTitle')}>
                        {getStatusIcon(esim.status)}
                        {esim.status || 'purchased'}
                      </span>
                    </div>
                  </div>

                  {/* Details Grid */}
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-5">
                    <div className="rounded-lg border border-gray-200 p-3 bg-gray-50/60">
                      <div className="text-[11px] uppercase tracking-wide text-gray-500 mb-1">ICCID</div>
                      <div className="text-sm font-mono text-gray-900 break-all" title={esim.iccid}>{esim.iccid}</div>
                    </div>
                    <div className="rounded-lg border border-gray-200 p-3 bg-gray-50/60">
                      <div className="text-[11px] uppercase tracking-wide text-gray-500 mb-1">{t('profile.item.pricePaid')}</div>
                      <div className="text-sm font-semibold text-green-600">${(Number(esim.price_paid)?.toFixed(2)) || '0.00'} {t('profile.common.usd')}</div>
                    </div>
                    <div className="rounded-lg border border-gray-200 p-3 bg-gray-50/60">
                      <div className="text-[11px] uppercase tracking-wide text-gray-500 mb-1">{t('profile.item.purchaseDate')}</div>
                      <div className="text-sm text-gray-900">{formatDate(esim.purchased_at)}</div>
                    </div>
                  </div>

                  {/* Action Buttons */}
                  <div className="flex flex-wrap gap-2">
                    <button onClick={() => handleShowDetails(esim)} className="inline-flex items-center px-3 py-2 bg-white text-purple-600 border border-purple-600 rounded-lg hover:bg-purple-50 transition-colors text-sm shadow-sm">
                      <Eye className="w-4 h-4 mr-1" />
                      {t('profile.actions.details')}
                    </button>
                    {esim.qr_code_data && (
                      <button onClick={() => handleShowQr(esim)} className="inline-flex items-center px-3 py-2 bg-white text-purple-600 border border-purple-600 rounded-lg hover:bg-purple-50 transition-colors text-sm shadow-sm">
                        <QrCode className="w-4 h-4 mr-1" />
                        {t('profile.actions.showQr')}
                      </button>
                    )}
                    <button onClick={() => downloadInvoice(esim)} className="inline-flex items-center px-3 py-2 bg-gray-100 text-gray-800 rounded-lg hover:bg-gray-200 transition-colors text-sm border border-gray-200">
                      <Download className="w-4 h-4 mr-1" />
                      {t('profile.actions.invoice')}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Help hint */}
        <div className="max-w-6xl mx-auto mt-6 text-xs text-gray-500">
          {t('profile.hint')}
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteModal && esimToDelete && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4" role="dialog" aria-modal="true">
          <div className="bg-white rounded-2xl max-w-md w-full p-6 border border-gray-200 shadow-xl">
            <div className="flex items-center mb-4">
              <div className="w-10 h-10 bg-red-100 rounded-xl flex items-center justify-center mr-3">
                <AlertCircle className="w-5 h-5 text-red-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900">{t('profile.delete.title')}</h3>
            </div>

            <p className="text-gray-600 mb-6">{t('profile.delete.confirm')}</p>

            <div className="bg-gray-50 border border-gray-200 p-3 rounded-xl mb-6">
              <div className="text-sm"><strong>{esimToDelete.country_name || esimToDelete.country_code}</strong> — {esimToDelete.bundle_name}</div>
              <div className="text-xs text-gray-500 mt-1">ICCID: {esimToDelete.iccid}</div>
            </div>

            <div className="flex gap-3">
              <button
                onClick={confirmDeleteEsim}
                disabled={deletingEsim}
                className="flex-1 bg-red-600 hover:bg-red-700 disabled:bg-red-400 text-white py-2.5 px-4 rounded-xl transition-colors font-medium disabled:cursor-not-allowed shadow-sm"
              >
                {deletingEsim ? t('profile.delete.deleting') : t('profile.delete.cta')}
              </button>
              <button
                onClick={() => { setShowDeleteModal(false); setEsimToDelete(null); }}
                className="flex-1 bg-gray-200 text-gray-800 py-2.5 px-4 rounded-xl hover:bg-gray-300 transition-colors"
              >
                {t('profile.common.cancel')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* eSIM Details Modal */}
      {showDetailsModal && selectedEsim && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4" role="dialog" aria-modal="true">
          <div className="bg-white rounded-2xl w-full max-w-5xl max-h-[90vh] overflow-y-auto border border-gray-200 shadow-xl">
            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 rounded-t-2xl">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 bg-blue-100 rounded-xl flex items-center justify-center">
                    <Smartphone className="w-5 h-5 text-blue-600" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">{t('profile.details.title')}</h3>
                    <p className="text-sm text-gray-500">{selectedEsim.country_name || selectedEsim.country_code}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={handleRefreshBundleStatus}
                    disabled={loadingBundleStatus}
                    className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-50"
                    title={t('profile.details.refresh')}
                  >
                    <RefreshCw className={`w-4 h-4 ${loadingBundleStatus ? 'animate-spin' : ''}`} />
                  </button>
                  <button
                    onClick={() => setShowDetailsModal(false)}
                    className="text-gray-400 hover:text-gray-600 p-2 hover:bg-gray-100 rounded-lg transition-colors"
                    aria-label={t('profile.common.close')}
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>
              </div>
            </div>

            <div className="p-6 space-y-6">
              {/* Live Bundle Status */}
              <div className="bg-gradient-to-r from-blue-50 to-purple-50 border border-blue-200 rounded-xl p-4">
                <div className="flex items-center justify-between mb-3">
                  <h4 className="font-semibold text-blue-900 flex items-center">
                    <Activity className="w-4 h-4 mr-2" />
                    {t('profile.details.live')}
                  </h4>
                  {bundleStatusData?.bundle_status?.last_updated && (
                    <div className="text-xs text-blue-600">
                      {t('profile.details.updated')} {new Date(bundleStatusData.bundle_status.last_updated).toLocaleTimeString()}
                    </div>
                  )}
                </div>

                {loadingBundleStatus && (
                  <div className="flex items-center justify-center py-8">
                    <div className="flex items-center space-x-3">
                      <Loader2 className="w-5 h-5 animate-spin text-blue-600" />
                      <span className="text-blue-800">{t('profile.details.fetching')}</span>
                    </div>
                  </div>
                )}

                {bundleStatusError && (
                  <div className="bg-red-50 border border-red-200 text-red-700 p-3 rounded-lg">
                    <div className="flex items-center">
                      <AlertCircle className="w-4 h-4 mr-2" />
                      {bundleStatusError}
                    </div>
                  </div>
                )}

                {bundleStatusData && !loadingBundleStatus && (
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <span className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium ${getBundleStateColor(bundleStatusData.bundle_status.bundle_state)}`}>
                        {getBundleStateIcon(bundleStatusData.bundle_status.bundle_state)}
                        {bundleStatusData.bundle_status.bundle_state?.toUpperCase() || 'UNKNOWN'}
                      </span>
                      {bundleStatusData.bundle_status.assignment_reference && (
                        <div className="text-xs text-blue-600 font-mono">
                          {t('profile.details.ref')} {bundleStatusData.bundle_status.assignment_reference}
                        </div>
                      )}
                    </div>

                    {bundleStatusData.bundle_status.initial_quantity > 0 && (
                      <div className="bg-white rounded-lg p-4 border border-blue-200">
                        <div className="flex justify-between items-center mb-2">
                          <span className="text-sm font-medium text-gray-900">{t('profile.details.usage.title')}</span>
                          <span className="text-sm text-gray-600">{bundleStatusData.bundle_status.usage_percent}%</span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-2 mb-2">
                          <div
                            className="bg-gradient-to-r from-blue-500 to-purple-500 h-2 rounded-full transition-all duration-500"
                            style={{ width: `${Math.min(bundleStatusData.bundle_status.usage_percent, 100)}%` }}
                          />
                        </div>
                        <div className="grid grid-cols-3 gap-4 text-xs">
                          <div>
                            <div className="text-gray-500">{t('profile.details.usage.used')}</div>
                            <div className="font-semibold text-gray-900">{bundleStatusData.bundle_status.used_quantity_formatted}</div>
                          </div>
                          <div>
                            <div className="text-gray-500">{t('profile.details.usage.remaining')}</div>
                            <div className="font-semibold text-green-600">{bundleStatusData.bundle_status.remaining_quantity_formatted}</div>
                          </div>
                          <div>
                            <div className="text-gray-500">{t('profile.details.usage.total')}</div>
                            <div className="font-semibold text-gray-900">{bundleStatusData.bundle_status.initial_quantity_formatted}</div>
                          </div>
                        </div>
                      </div>
                    )}

                    {bundleStatusData.bundle_status.assignment_date && (
                      <div className="bg-white rounded-lg p-3 border border-blue-200">
                        <div className="text-xs text-gray-500 mb-1">{t('profile.details.assigned')}</div>
                        <div className="text-sm font-medium text-gray-900">
                          {formatFullDate(bundleStatusData.bundle_status.assignment_date)}
                        </div>
                      </div>
                    )}

                    {bundleStatusData.bundle_status.message && (
                      <div className="bg-yellow-50 border border-yellow-200 text-yellow-800 p-3 rounded-lg text-sm">
                        {bundleStatusData.bundle_status.message}
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Status Badge + Discount */}
              <div className="flex items-center justify-between">
                <span className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium ${getStatusColor(selectedEsim.status)}`}>
                  {getStatusIcon(selectedEsim.status)}
                  {selectedEsim.status || 'purchased'}
                </span>
                {selectedEsim.discount_applied && (
                  <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                    <CheckCircle className="w-3 h-3" />
                    {t('profile.details.discount')}
                  </span>
                )}
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div className="space-y-6">
                  {/* Plan Info */}
                  <div className="bg-gray-50 rounded-xl p-4">
                    <h4 className="font-medium text-gray-900 mb-3 flex items-center">
                      <Globe className="w-4 h-4 mr-2" />
                      {t('profile.details.plan.title')}
                    </h4>
                    <div className="grid grid-cols-1 gap-4">
                      <div>
                        <div className="text-xs text-gray-500 mb-1">{t('profile.details.plan.destination')}</div>
                        <div className="text-sm font-medium text-gray-900">{selectedEsim.country_name || selectedEsim.country_code}</div>
                      </div>
                      <div>
                        <div className="text-xs text-gray-500 mb-1">{t('profile.details.plan.name')}</div>
                        <div className="text-sm font-medium text-gray-900">{selectedEsim.bundle_name}</div>
                      </div>
                      {selectedEsim.data_amount && (
                        <div>
                          <div className="text-xs text-gray-500 mb-1">{t('profile.details.plan.allowance')}</div>
                          <div className="text-sm font-medium text-gray-900">{selectedEsim.data_amount}</div>
                        </div>
                      )}
                      {selectedEsim.validity_days && (
                        <div>
                          <div className="text-xs text-gray-500 mb-1">{t('profile.details.plan.validity')}</div>
                          <div className="text-sm font-medium text-gray-900">{selectedEsim.validity_days} {t('profile.common.days')}</div>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Technical */}
                  <div className="bg-gray-50 rounded-xl p-4">
                    <h4 className="font-medium text-gray-900 mb-3 flex items-center">
                      <Database className="w-4 h-4 mr-2" />
                      {t('profile.details.tech.title')}
                    </h4>
                    <div className="space-y-3">
                      <div>
                        <div className="text-xs text-gray-500 mb-1">ICCID</div>
                        <div className="flex items-center justify-between bg-white border border-gray-200 rounded-lg p-3">
                          <code className="text-sm font-mono text-gray-900 break-all">{selectedEsim.iccid}</code>
                          <button
                            onClick={() => copyToClipboard(selectedEsim.iccid, 'profile.details.tech.iccid')}
                            className="ml-2 p-1 text-gray-400 hover:text-gray-600 flex-shrink-0"
                            title={t('profile.details.tech.copyIccid')}
                          >
                            <Database className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                      {selectedEsim.qr_code_data && (
                        <div>
                          <div className="text-xs text-gray-500 mb-1">{t('profile.details.tech.activation')}</div>
                          <div className="flex items-center justify-between bg-white border border-gray-200 rounded-lg p-3">
                            <code className="text-sm font-mono text-gray-900 break-all pr-2">{selectedEsim.qr_code_data}</code>
                            <button
                              onClick={() => copyToClipboard(selectedEsim.qr_code_data, 'profile.details.tech.activation')}
                              className="ml-2 p-1 text-gray-400 hover:text-gray-600 flex-shrink-0"
                              title={t('profile.details.tech.copyActivation')}
                            >
                              <QrCode className="w-4 h-4" />
                            </button>
                          </div>
                        </div>
                      )}
                      {selectedEsim.goesim_order_reference && (
                        <div>
                          <div className="text-xs text-gray-500 mb-1">{t('profile.details.tech.orderRef')}</div>
                          <div className="bg-white border border-gray-200 rounded-lg p-3">
                            <code className="text-sm font-mono text-gray-900">{selectedEsim.goesim_order_reference}</code>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Install Info */}
                  <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
                    <h4 className="font-medium text-blue-900 mb-3 flex items-center">
                      <Info className="w-4 h-4 mr-2" />
                      {t('profile.details.install.title')}
                    </h4>
                    <div className="space-y-3 text-sm text-blue-800">
                      <div>
                        <div className="font-medium mb-1">{t('profile.details.install.iosTitle')}</div>
                        <div>{t('profile.details.install.ios')}</div>
                      </div>
                      <div>
                        <div className="font-medium mb-1">{t('profile.details.install.androidTitle')}</div>
                        <div>{t('profile.details.install.android')}</div>
                      </div>
                      <div className="text-xs text-blue-600 bg-blue-100 p-2 rounded-lg">
                        <strong>{t('profile.details.install.important')}</strong> {t('profile.details.install.once')}
                      </div>
                    </div>
                  </div>
                </div>

                <div className="space-y-6">
                  {/* Purchase */}
                  <div className="bg-gray-50 rounded-xl p-4">
                    <h4 className="font-medium text-gray-900 mb-3 flex items-center">
                      <CreditCard className="w-4 h-4 mr-2" />
                      {t('profile.details.purchase.title')}
                    </h4>
                    <div className="grid grid-cols-1 gap-4">
                      <div>
                        <div className="text-xs text-gray-500 mb-1">{t('profile.details.purchase.date')}</div>
                        <div className="text-sm font-medium text-gray-900">{formatFullDate(selectedEsim.purchased_at)}</div>
                      </div>
                      <div>
                        <div className="text-xs text-gray-500 mb-1">{t('profile.details.purchase.amount')}</div>
                        <div className="text-sm font-semibold text-green-600">${(Number(selectedEsim.price_paid)?.toFixed(2)) || '0.00'} {t('profile.common.usd')}</div>
                      </div>
                      {selectedEsim.payment_method && (
                        <div>
                          <div className="text-xs text-gray-500 mb-1">{t('profile.details.purchase.method')}</div>
                          <div className="text-sm font-medium text-gray-900 capitalize">{selectedEsim.payment_method}</div>
                        </div>
                      )}
                      {selectedEsim.purchase_id && (
                        <div>
                          <div className="text-xs text-gray-500 mb-1">{t('profile.details.purchase.txn')}</div>
                          <div className="text-sm font-mono text-gray-900">{selectedEsim.purchase_id}</div>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Referral */}
                  {selectedEsim.referral_code_used && (
                    <div className="bg-green-50 border border-green-200 rounded-xl p-4">
                      <h4 className="font-medium text-green-900 mb-3 flex items-center">
                        <CheckCircle className="w-4 h-4 mr-2" />
                        {t('profile.details.referral.title')}
                      </h4>
                      <div className="grid grid-cols-1 gap-4">
                        <div>
                          <div className="text-xs text-green-600 mb-1">{t('profile.details.referral.code')}</div>
                          <div className="text-sm font-bold text-green-900">{selectedEsim.referral_code_used}</div>
                        </div>
                        {selectedEsim.original_price && (
                          <div>
                            <div className="text-xs text-green-600 mb-1">{t('profile.details.referral.original')}</div>
                            <div className="text-sm font-medium text-green-900">${selectedEsim.original_price?.toFixed(2)}</div>
                          </div>
                        )}
                        {selectedEsim.discount_amount && (
                          <div>
                            <div className="text-xs text-green-600 mb-1">{t('profile.details.referral.saved')}</div>
                            <div className="text-sm font-bold text-green-900">${selectedEsim.discount_amount?.toFixed(2)}</div>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Billing */}
                  {selectedEsim.billing_details && (
                    <div className="bg-gray-50 rounded-xl p-4">
                      <h4 className="font-medium text-gray-900 mb-3 flex items-center">
                        <User className="w-4 h-4 mr-2" />
                        {t('profile.details.billing.title')}
                      </h4>
                      <div className="grid grid-cols-1 gap-4">
                        {selectedEsim.billing_details.fullName && (
                          <div>
                            <div className="text-xs text-gray-500 mb-1">{t('profile.details.billing.name')}</div>
                            <div className="text-sm font-medium text-gray-900">{selectedEsim.billing_details.fullName}</div>
                          </div>
                        )}
                        {selectedEsim.billing_details.email && (
                          <div>
                            <div className="text-xs text-gray-500 mb-1">{t('profile.details.billing.email')}</div>
                            <div className="text-sm font-medium text-gray-900">{selectedEsim.billing_details.email}</div>
                          </div>
                        )}
                        {selectedEsim.billing_details.country && (
                          <div>
                            <div className="text-xs text-gray-500 mb-1">{t('profile.details.billing.country')}</div>
                            <div className="text-sm font-medium text-gray-900">{selectedEsim.billing_details.country}</div>
                          </div>
                        )}
                        {selectedEsim.billing_details.zipCode && (
                          <div>
                            <div className="text-xs text-gray-500 mb-1">{t('profile.details.billing.zip')}</div>
                            <div className="text-sm font-medium text-gray-900">{selectedEsim.billing_details.zipCode}</div>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Modal Actions */}
            <div className="sticky bottom-0 bg-white border-t border-gray-200 px-6 py-4 rounded-b-2xl">
              <div className="flex justify-end">
                <button
                  onClick={() => setShowDetailsModal(false)}
                  className="px-6 py-2.5 bg-gray-100 text-gray-800 rounded-lg hover:bg-gray-200 transition-colors text-sm font-medium border border-gray-200"
                >
                  {t('profile.common.close')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* QR Code Modal */}
      {showQrModal && selectedEsim && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4" role="dialog" aria-modal="true">
          <div className="bg-white rounded-2xl max-w-md w-full p-6 border border-gray-200 shadow-xl">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">{t('profile.qr.title')}</h3>
              <button
                onClick={() => setShowQrModal(false)}
                className="text-gray-400 hover:text-gray-600"
                aria-label={t('profile.common.close')}
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="text-center">
              <div className="mb-4">
                <h4 className="font-medium text-gray-900">{selectedEsim.country_name || selectedEsim.country_code}</h4>
                <p className="text-sm text-gray-500">{selectedEsim.bundle_name}</p>
              </div>

              {selectedEsim.qr_code_base64 ? (
                <div className="mb-4">
                  <img
                    src={`data:image/png;base64,${selectedEsim.qr_code_base64}`}
                    alt={t('profile.qr.alt')}
                    className="mx-auto border rounded-xl"
                    width="220"
                    height="220"
                  />
                </div>
              ) : (
                <div className="w-52 h-52 mx-auto bg-gray-100 rounded-xl flex items-center justify-center mb-4 border border-gray-200">
                  <QrCode className="w-16 h-16 text-gray-400" />
                </div>
              )}

              {selectedEsim.qr_code_data && (
                <div className="bg-gray-50 border border-gray-200 p-3 rounded-xl mb-4 text-left">
                  <div className="text-xs text-gray-500 mb-1">{t('profile.details.tech.activation')}</div>
                  <div className="text-sm font-mono text-gray-900 break-all" title={selectedEsim.qr_code_data}>{selectedEsim.qr_code_data}</div>
                  <button
                    onClick={() => copyToClipboard(selectedEsim.qr_code_data, 'profile.details.tech.activation')}
                    className="mt-2 text-xs text-blue-600 hover:text-blue-800 font-medium"
                  >
                    {t('profile.qr.copy')}
                  </button>
                </div>
              )}

              <div className="text-xs text-gray-500 mb-4 max-w-sm mx-auto">
                {t('profile.qr.tip')}
              </div>

              <button
                onClick={() => setShowQrModal(false)}
                className="w-full bg-blue-600 hover:bg-blue-700 text-white py-2.5 px-4 rounded-xl transition-colors shadow-sm"
              >
                {t('profile.common.close')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Profile;
